import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  Link,
  Typography,
} from '@mui/material';
import axios from 'axios';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API_ENDPOINTS from '../config/api';
import { useAuth } from '../contexts/AuthContext';


const FONT_FAMILY =
  'Poppins, Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif';

const ConsentPage: React.FC = () => {
  const { refreshUser, logout } = useAuth();
  const navigate = useNavigate();

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [aiConsent, setAiConsent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = termsAccepted && aiConsent && !saving;

  const handleSubmit = async (): Promise<void> => {
    try {
      setSaving(true);
      setError(null);

      await axios.patch(API_ENDPOINTS.AUTH.PROFILE, {
        terms_accepted: true,
        ai_consent: true,
      });

      // Reason: re-fetch user so consent_required flips to false and gate unlocks.
      await refreshUser();
      navigate('/home');
    } catch (err: any) {
      console.error('Failed to submit consent:', err);
      setError(
        err.response?.data?.detail ||
        'Something went wrong. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container
      component="main"
      maxWidth="xs"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        py: { xs: 6, sm: 10 },
      }}
    >
      <Box sx={{ width: '100%' }}>
        <Typography
          component="h1"
          sx={{
            fontFamily: FONT_FAMILY,
            fontWeight: 700,
            fontSize: { xs: 32, sm: 38 },
            lineHeight: 1.3,
            mb: 2,
          }}
        >
          Updated Terms
        </Typography>

        <Typography
          sx={{
            fontFamily: FONT_FAMILY,
            fontSize: 15,
            color: '#555',
            mb: 3,
            lineHeight: 1.6,
          }}
        >
          We have updated our Terms of Use and Privacy Policy. Please review
          and accept them to continue using Jeff Says.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            bgcolor: '#F9F9F9',
            borderRadius: '16px',
            p: 2.5,
            mb: 3,
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                disabled={saving}
                sx={{ '&.Mui-checked': { color: '#FFD400' } }}
              />
            }
            label={
              <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: 14 }}>
                I accept the{' '}
                <Link
                  href="https://jeffsays.com/terms-of-use.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: '#1976d2' }}
                >
                  Terms of Use
                </Link>{' '}
                and{' '}
                <Link
                  href="https://jeffsays.com/privacy-policy.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: '#1976d2' }}
                >
                  Privacy Policy
                </Link>
              </Typography>
            }
            sx={{ mb: 1.5, alignItems: 'center' }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={aiConsent}
                onChange={(e) => setAiConsent(e.target.checked)}
                disabled={saving}
                sx={{ '&.Mui-checked': { color: '#FFD400' } }}
              />
            }
            label={
              <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: 14 }}>
                I consent to my chat messages, contextual metadata, and
                relevant conversation context being transmitted to OpenAI to
                generate responses and to perform conversation analysis and
                summarisation. We do not opt in to OpenAI using our API data
                for model training or product improvement.
              </Typography>
            }
            sx={{ alignItems: 'flex-start' }}
          />
        </Box>

        <Button
          variant="contained"
          fullWidth
          onClick={handleSubmit}
          disabled={!canSubmit}
          sx={{
            bgcolor: '#FFD400',
            color: '#000000',
            fontFamily: FONT_FAMILY,
            fontWeight: 700,
            borderRadius: '12px',
            py: 1.75,
            textTransform: 'none',
            fontSize: 16,
            mb: 1.5,
            '&:hover': { bgcolor: '#E6BF00' },
            '&.Mui-disabled': {
              bgcolor: '#E0E0E0',
              color: '#999',
            },
          }}
        >
          {saving ? <CircularProgress size={24} /> : 'Continue'}
        </Button>

        <Button
          variant="outlined"
          fullWidth
          onClick={() => {
            logout();
            navigate('/login');
          }}
          disabled={saving}
          sx={{
            color: '#666',
            borderColor: '#CCC',
            fontFamily: FONT_FAMILY,
            fontWeight: 600,
            borderRadius: '12px',
            py: 1.5,
            textTransform: 'none',
            fontSize: 15,
            '&:hover': {
              borderColor: '#999',
              bgcolor: 'rgba(0, 0, 0, 0.03)',
            },
          }}
        >
          Decline
        </Button>
      </Box>
    </Container>
  );
};

export default ConsentPage;
